import { Stack, TextField, Alert, Box, useTheme, LinearProgress } from "@mui/material"
import Avatar from "../Tools/Avatar"
import { useState, useEffect } from "react"
import FormButtons from "../Inputs/FormButtons"
import API from "../../services/API"
import QuestionIcon from '@mui/icons-material/LiveHelp';
import EditIcon from '@mui/icons-material/Edit';
import BugIcon from '@mui/icons-material/BugReport';
import Tip from "../Tools/Tip"
import Toggle from "../Inputs/Toggle"
import CourseSelect from "../Inputs/CourseSelect"
import Pictures from "../Inputs/Pictures"
import ReactQuill from "react-quill"
import '../../css/social.css';
import Loader from "../Tools/Loader"
import { useContext } from "react"
import UserContext from "../../services/Context/UserContext"
import Tags from "../Inputs/Tags"
import { txt } from "../../services/translation"

export default function PostForm({id, embed, section, courseId, sectionId, type, onSubmit, onClose}){

    const isNew = (id || 'new') === 'new';

    const {user} = useContext(UserContext)

    const [post, setPost] = useState(null)
    const [pictures, setPictures] = useState([])

    const [status, setStatus] = useState('loading')
    const statusIs = str => status === str
    const statusIn = arr => arr.includes(status)

    useEffect(() => {

        if(isNew){

            let newPost = defaultPost();

            newPost.course = courseId ? {id: courseId} : null;

            newPost.section = sectionId ? {id: sectionId} : null;

            newPost.class = type || null;

            const categoryTag = user.tags?.find(t => t.type?.id === 'category');

            if(categoryTag) newPost.tags.push(categoryTag);
            
            if(section){

                API.get(`/social/sections/${section}`)
                    .then(r => {
                        const s = r.data;
                        
                        newPost.title = s.title;
                        
                        if(s.courseTag){
                            newPost.course = newPost.course || {id: s.courseTag.code};
                            newPost.tags.push(s.courseTag);
                        }

                        setPost(newPost);
                        setStatus('ready')
                    })
                    .catch(() => {
                        setPost(newPost)
                        setStatus('ready')
                    })

            }else{
                setPost(newPost);
                setStatus('ready')
            }

            
        }else{
            API.get(`/social/posts/${id}`)
                .then(r => {
                    let pics = (r.data.pictures || [])
                        .map(p => {
                            return {url: p, uploaded: true}
                        })
                    
                    setPictures(pics);

                    setPost(r.data)
                    setStatus((!r.data?.mine && !user.admin) ? 'unauthorized' : 'ready')
                })
                .catch(e => setStatus('notFound'))
        }
        

    }, [id, isNew, section, user, courseId, sectionId, type])



    const handleSubmit = e => {
        e.preventDefault();

        (isNew ? 
            API.post(`/social/posts`, post) : 
            API.put(`/social/posts/${post.id}`, post)
        )
            .then(r => uploadPictures(r.data).finally(() => handleSuccess()))
            .catch(e => handleError(e))

        return setStatus('submit');
    }



    const uploadPictures = async post => {

        let files = pictures.filter(p => !p.uploaded);

        if(files.length > 0){

            let formData = new FormData();
            files.forEach((f,i) => formData.append(`picture_${i}`, f));

            try{
                return await API.upload(`/social/posts/${post.id}/pictures`, formData);
            }catch(e){
                return e;
            }
        }

        return false;
    }



    const handleSuccess = r => {
        console.log(r?.data);
        setTimeout(() => onSubmit(), 4000);
        return setStatus('success');
    }


    const handleError = e => {
        console.log(e?.response?.data);
        return setStatus('error');
    }



    const handleClassChange = value => {
        let copy = {...post};
        copy.title = copy.title || (value === 'bug' ? txt('bugReport') : '');
        copy.class = value;
        return setPost(copy);
    }



    const handleCourseChange = id => {
        if(!id) return false;
        
        API.get(`/social/courses/${id}/tag`)
            .then(r => {
                let copy = {...post};
                copy.course = {id: id};
                copy.tags = copy.tags.filter(t => t.type?.id !== 'course')
                copy.tags.push(r.data);
                setPost(copy);
            })

        let copy = {...post};
        copy.course = {id: id};
        return setPost(copy);
    }



    const handlePictureDelete = pic => {
        if(pic.uploaded){

            const fileName = pic.url.split('/').pop();

            API.delete(`/social/posts/pictures/${fileName}`)
                .then(r => console.log(r.data))
                .catch(e => console.log(e));

        }else{
            return false;
        }
    }


    return <div className={!embed ? 'social-box' : ''}>

        <Top onClose={onClose} />

        {statusIs('notFound') && 
            <Alert severity='warning'>{txt('postNotFound')}</Alert>
        }

        {statusIs('unauthorized') && 
            <Alert severity='warning'>{txt('unauthorizedModification')}</Alert>
        }


        {statusIs('loading') && 
            <Loader />
        }

        {statusIn(['ready','submit','error'])  && <>
            
            {post.class &&
                        
                <form onSubmit={handleSubmit}>

                    <Stack direction='column' spacing={3}>

                        {statusIs('error') && 
                            <Alert severity='error'>
                                {txt('postSaveError')}
                            </Alert>
                        }

                        <TextField
                            label={txt('postTitle')}
                            value={post.title || ''}
                            onChange={e => setPost({...post, title: e.target.value})}
                            size='small'
                            inputProps={{
                                minLength: 3,
                                maxLength: 300
                            }}
                            autoFocus
                            required
                        />


                        {post.class === 'question' && 
                            <CourseSelect 
                                value={post.course?.id}
                                onChange={id => handleCourseChange(id)}
                                required
                                disabled={courseId ? true : false}
                            />
                        }


                        {post.class !== 'post' && <TextField
                            label={txt('content')}
                            value={post.content || ''}
                            onChange={e => setPost({...post, content: e.target.value})}
                            inputProps={{
                                minLength: 3,
                                maxLength: 5000
                            }}
                            required
                            multiline
                            minRows={3}
                        />}

                        {post.class === 'post' && <ReactQuill 
                            value={post.content} 
                            onChange={html => setPost({...post, content: html})}
                        />}

                        {false &&
                            <Tags
                                label={txt('addKeywordsLabel')}
                                value={post.tags || []}
                                onChange={tags => setPost({...post, tags: tags})}
                            />
                        }

                        <Toggle 
                            label={txt('publishAnonymouslyLabel')}
                            value={post.anonymous}
                            onChange={() => setPost({...post, anonymous: !post.anonymous})}
                        />

                        <Pictures
                            value={pictures}
                            onChange={files => setPictures(files)}
                            onDelete={file => handlePictureDelete(file)}
                            maxSize={100000000}
                        />

                        <FormButtons 
                            submitText={txt('publish')} 
                            loading={statusIs('submit')}
                            onCancel={onClose}
                        />

                    </Stack>

                </form>
                
            }
   
                    
            {!post.class &&
                <ClassSelect onSelect={value => handleClassChange(value)}/>
            }

                    
        </>}


        {statusIs('success') && 
            <Box>

                <LinearProgress color='success' />

                <Alert severity='success'>
                    {txt('postSuccessMessage')} {post.class === 'question' ? txt('postSuccessMessage2') : ''}
                </Alert>

            </Box>
        }


    </div>
}


function defaultPost(){

    return {
        title: '',
        content: '',
        tags: [],
        class: null,
        anonymous: false,
        mine: true
    }
}


function Top({onClose}){

    const {user} = useContext(UserContext)
 
    return <Stack direction='row' justifyContent='space-between' sx={{mb: 4}}>

        {user ? 
            <Stack direction='row'>

                <Avatar 
                    initials={user.initials} 
                    color={user.category?.color} 
                />

                <span style={{marginLeft: 10}}>
                    <b>{user.name}</b>
                    <br/>
                    <small>{txt('today')}</small>
                </span>

            </Stack>
            :
            <div></div>
        }


        <Tip type='close' onClick={onClose} />

    </Stack>

}


function ClassSelect({ onSelect }) {
    return (
        <Stack direction='column' spacing={2}>
            <h3 style={{ textAlign: 'center' }}>{txt('newPostQuestion')}</h3>

            <ClassItem
                text={txt('questionOption')}
                icon={<QuestionIcon sx={{ width: 22, height: 22 }} />}
                onClick={() => onSelect('question')}
            />

            <ClassItem
                text={txt('experienceOption')}
                icon={<EditIcon sx={{ width: 22, height: 22 }} />}
                onClick={() => onSelect('post')}
            />

            <ClassItem
                text={txt('platformIssueOption')}
                icon={<BugIcon sx={{ width: 22, height: 22 }} />}
                onClick={() => onSelect('bug')}
            />
        </Stack>
    );
}



function ClassItem({icon, onClick, text}){

    const theme = useTheme();

    const color = theme.palette.primary.main;

    const sx = {
        color: color,
        p: 1,
        border: 'solid 1px',
        borderColor: theme.palette.primary,
        borderRadius: '5px',
        mb: 2,
        cursor: 'pointer',
        textAlign: 'center'
    }

    return <Stack direction='row' justifyContent='center' spacing={1} sx={sx} onClick={onClick}>
        {icon}
        <Box>{text}</Box>
    </Stack>
}